/**
 * Portal invoice line items — shared normalizer + money helpers.
 *
 * Monetary semantics (canonical, post-normalize):
 * - `quantity` × `unitAmount` = `amount` (line subtotal), all rounded with roundSgd2.
 * - Line `amount` / `unitAmount` use the same convention as createPortalInvoice `input.amount`:
 *   the invoice **base** before GST and card fee (ex-GST when GST will be applied).
 * - Invoice payable total remains `computeInvoiceAmounts(base, …).totalPayable` stored in
 *   `portal_invoices.amountInclGst`.
 *
 * Legacy rows `{ description, amount }` (no quantity/unitAmount):
 * - Remain valid. Normalized as qty=1, unitAmount=amount, amount=amount.
 * - Historically those `amount` values often mirrored the payable total (esp. imports).
 *   Xero mapping detects legacy-vs-canonical via an explicit `basis` flag written on
 *   new saves, falling back to shape heuristics for older JSON.
 */

export const PORTAL_INVOICE_LINE_MONEY_EPS = 0.01;

export type PortalInvoiceLineItemBasis = "invoice_base" | "legacy_as_stored";

/** Wire / DB JSON shape (backward compatible). */
export type PortalInvoiceLineItem = {
  id?: string;
  description: string;
  quantity?: string | number;
  unitAmount?: string;
  amount?: string;
  /** Set on newly persisted canonical rows; absent on legacy imports. */
  basis?: PortalInvoiceLineItemBasis;
};

export type NormalizedPortalInvoiceLineItem = {
  id?: string;
  description: string;
  quantity: number;
  unitAmount: string;
  amount: string;
  basis: PortalInvoiceLineItemBasis;
};

export type PortalInvoiceLineItemInput = {
  id?: string;
  description: string;
  quantity?: string | number;
  unitAmount?: string;
  amount?: string;
};

export function roundSgd2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function moneyToFixed2(n: number): string {
  return roundSgd2(n).toFixed(2);
}

/**
 * Preserve intentional paragraphs (`\n`, `\r\n`, blank lines).
 * Normalize only unsafe/excessive horizontal whitespace; do not flatten line breaks.
 */
export function normalizePortalInvoiceDescription(text: string): string {
  return String(text ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/[^\S\n]+/g, " ")
    .replace(/ \n/g, "\n")
    .replace(/\n /g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/^\n+/, "")
    .replace(/\n+$/, "")
    .trimEnd();
}

function parsePositiveMoney(raw: unknown, label: string): number {
  if (raw == null || String(raw).trim() === "") {
    throw new Error(`${label} is required`);
  }
  const n = Number(raw);
  if (!Number.isFinite(n)) throw new Error(`${label} is invalid`);
  const rounded = roundSgd2(n);
  if (!(rounded > 0)) throw new Error(`${label} must be positive`);
  return rounded;
}

function parsePositiveQuantity(raw: unknown): number {
  if (raw == null || String(raw).trim() === "") return 1;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) {
    throw new Error("Quantity must be a positive number");
  }
  // Allow fractional qty; keep up to 4 dp then money-round the product.
  return Math.round(n * 10000) / 10000;
}

function looksCanonical(raw: PortalInvoiceLineItemInput): boolean {
  return raw.quantity != null || (raw.unitAmount != null && String(raw.unitAmount).trim() !== "");
}

/**
 * Normalize one raw line. Throws on empty description / invalid money / non-positive totals.
 */
export function normalizePortalInvoiceLineItem(
  raw: PortalInvoiceLineItemInput,
  opts?: { index?: number; forceBasis?: PortalInvoiceLineItemBasis },
): NormalizedPortalInvoiceLineItem {
  const indexLabel = opts?.index != null ? `Line ${opts.index + 1}` : "Line item";
  const description = normalizePortalInvoiceDescription(raw.description);
  if (!description.trim()) {
    throw new Error(`${indexLabel}: description is required`);
  }

  const canonical = looksCanonical(raw);
  const basis: PortalInvoiceLineItemBasis =
    opts?.forceBasis ?? (canonical ? "invoice_base" : "legacy_as_stored");

  let quantity = 1;
  let unitAmountNum: number;
  let amountNum: number;

  if (canonical) {
    quantity = parsePositiveQuantity(raw.quantity);
    if (raw.unitAmount != null && String(raw.unitAmount).trim() !== "") {
      unitAmountNum = parsePositiveMoney(raw.unitAmount, `${indexLabel}: unit amount`);
      amountNum = roundSgd2(quantity * unitAmountNum);
      if (raw.amount != null && String(raw.amount).trim() !== "") {
        const clientAmount = parsePositiveMoney(raw.amount, `${indexLabel}: amount`);
        if (Math.abs(clientAmount - amountNum) > PORTAL_INVOICE_LINE_MONEY_EPS) {
          throw new Error(
            `${indexLabel}: amount (${moneyToFixed2(clientAmount)}) does not match quantity × unit amount (${moneyToFixed2(amountNum)})`,
          );
        }
      }
    } else if (raw.amount != null && String(raw.amount).trim() !== "") {
      amountNum = parsePositiveMoney(raw.amount, `${indexLabel}: amount`);
      unitAmountNum = roundSgd2(amountNum / quantity);
    } else {
      throw new Error(`${indexLabel}: unit amount or amount is required`);
    }
  } else {
    amountNum = parsePositiveMoney(raw.amount, `${indexLabel}: amount`);
    quantity = 1;
    unitAmountNum = amountNum;
  }

  if (!(amountNum > 0)) {
    throw new Error(`${indexLabel}: amount must be positive`);
  }

  return {
    id: raw.id?.trim() || undefined,
    description,
    quantity,
    unitAmount: moneyToFixed2(unitAmountNum),
    amount: moneyToFixed2(amountNum),
    basis,
  };
}

export function normalizePortalInvoiceLineItems(
  rawItems: PortalInvoiceLineItemInput[] | null | undefined,
  opts?: { forceBasis?: PortalInvoiceLineItemBasis },
): NormalizedPortalInvoiceLineItem[] {
  if (!rawItems?.length) return [];
  return rawItems.map((item, index) =>
    normalizePortalInvoiceLineItem(item, { index, forceBasis: opts?.forceBasis }),
  );
}

export function sumPortalInvoiceLineAmounts(items: Array<{ amount: string | number }>): number {
  return roundSgd2(items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0));
}

export function assertLineItemsSumToInvoiceBase(
  items: NormalizedPortalInvoiceLineItem[],
  invoiceBaseAmount: number,
): void {
  if (!items.length) {
    throw new Error("At least one line item is required");
  }
  const sum = sumPortalInvoiceLineAmounts(items);
  const base = roundSgd2(invoiceBaseAmount);
  if (Math.abs(sum - base) > PORTAL_INVOICE_LINE_MONEY_EPS) {
    throw new Error(
      `Line item total (${moneyToFixed2(sum)}) must equal invoice base amount (${moneyToFixed2(base)})`,
    );
  }
}

/** Top-level description snapshot for legacy/search/display — joins items, keeps newlines. */
export function buildPortalInvoiceDescriptionSnapshot(
  items: NormalizedPortalInvoiceLineItem[],
  fallback?: string,
): string {
  if (!items.length) {
    return normalizePortalInvoiceDescription(fallback ?? "");
  }
  if (items.length === 1) return items[0]!.description;
  return items.map((item, i) => `${i + 1}. ${item.description}`).join("\n\n");
}

export function toPortalInvoiceLineItemsJson(
  items: NormalizedPortalInvoiceLineItem[],
): PortalInvoiceLineItem[] {
  return items.map((item) => ({
    ...(item.id ? { id: item.id } : {}),
    description: item.description,
    quantity: item.quantity,
    unitAmount: item.unitAmount,
    amount: item.amount,
    basis: item.basis,
  }));
}

/** Public / hosted API mapping. */
export function mapPortalInvoiceLineItemsForPublic(
  lineItemsJson: PortalInvoiceLineItem[] | null | undefined,
  fallbackDescription: string,
  opts?: { invoicePayable?: number; gstApplied?: boolean },
): Array<{
  description: string;
  quantity: number;
  unitAmount: number;
  total: number;
}> {
  if (!lineItemsJson?.length) {
    const payable = opts?.invoicePayable;
    const gstApplied = opts?.gstApplied !== false;
    const total =
      payable != null && Number.isFinite(payable)
        ? gstApplied
          ? roundSgd2(Number(payable) / 1.09)
          : roundSgd2(Number(payable))
        : null;
    return [
      {
        description: normalizePortalInvoiceDescription(fallbackDescription) || "Invoice item",
        quantity: 1,
        unitAmount: total ?? 0,
        total: total ?? 0,
      },
    ];
  }

  return normalizePortalInvoiceLineItems(lineItemsJson).map((item) => {
    const amount = Number(item.amount);
    // Legacy rows stored payable-ish amounts: when basis is legacy_as_stored and gst on,
    // hosted view historically showed presentation ex-GST for the single primary line via
    // presentInvoiceGstBreakdown. Keep returning stored amount; InvoiceView presentation
    // still prefers GST presentation for single-row invoices.
    return {
      description: item.description,
      quantity: item.quantity,
      unitAmount: Number(item.unitAmount),
      total: amount,
    };
  });
}

/**
 * PDF display amounts for a normalized line, matching presentInvoiceGstBreakdown convention:
 * - gstApplied true → show ex-GST unit/line (invoice_base amounts as stored)
 * - gstApplied false → show inclusive unit/line (invoice_base == payable base)
 * Legacy payable-stored rows (basis legacy_as_stored + gstApplied): convert to ex-GST for display
 * when a single legacy amount matches the invoice payable.
 */
export function portalInvoiceLineAmountsForDisplay(
  item: NormalizedPortalInvoiceLineItem,
  opts: { gstApplied: boolean; invoicePayable: number },
): { quantity: number; unitAmount: number; lineAmount: number } {
  const qty = item.quantity > 0 ? item.quantity : 1;
  const stored = Number(item.amount);
  if (item.basis === "legacy_as_stored" && opts.gstApplied) {
    const payable = roundSgd2(opts.invoicePayable);
    if (Math.abs(stored - payable) <= PORTAL_INVOICE_LINE_MONEY_EPS) {
      const excl = roundSgd2(payable / 1.09);
      return { quantity: qty, unitAmount: roundSgd2(excl / qty), lineAmount: excl };
    }
  }
  return {
    quantity: qty,
    unitAmount: Number(item.unitAmount),
    lineAmount: stored,
  };
}

export type XeroLineDraft = {
  Description: string;
  Quantity: number;
  UnitAmount: number;
};

/**
 * Map normalized portal lines to Xero line drafts.
 * Preserves established LineAmountTypes semantics at the caller:
 * - gstApplied → Inclusive (UnitAmount is GST-inclusive)
 * - !gstApplied → NoTax (UnitAmount is the commercial amount)
 *
 * Legacy rows: Quantity 1, UnitAmount = stored amount (unchanged).
 * Canonical invoice_base rows: Quantity = qty; UnitAmount = unit × 1.09 when GST on.
 * Card-fee residual (if payable exceeds GST-inclusive line sum) becomes an extra line.
 */
export function mapPortalInvoiceLinesForXero(params: {
  items: NormalizedPortalInvoiceLineItem[];
  descriptionFallback: string;
  amountInclGst: number;
  gstApplied: boolean;
  cardAdminFeeApplied?: boolean;
}): XeroLineDraft[] {
  const payable = roundSgd2(params.amountInclGst);
  const gstApplied = params.gstApplied;

  if (!params.items.length) {
    return [
      {
        Description: (params.descriptionFallback || "Portal invoice").slice(0, 4000),
        Quantity: 1,
        UnitAmount: payable,
      },
    ];
  }

  const allLegacy = params.items.every((i) => i.basis === "legacy_as_stored");
  if (allLegacy) {
    return params.items.map((item) => ({
      Description: (item.description || params.descriptionFallback || "Invoice").slice(0, 4000),
      Quantity: 1,
      UnitAmount: Number(item.amount),
    }));
  }

  const lines: XeroLineDraft[] = params.items.map((item) => {
    const qty = item.quantity > 0 ? item.quantity : 1;
    const unitBase = Number(item.unitAmount);
    const unitXero = gstApplied ? roundSgd2(unitBase * 1.09) : unitBase;
    return {
      Description: (item.description || params.descriptionFallback || "Invoice").slice(0, 4000),
      Quantity: qty,
      UnitAmount: unitXero,
    };
  });

  const linesSum = roundSgd2(lines.reduce((s, l) => s + roundSgd2(l.Quantity * l.UnitAmount), 0));
  const residual = roundSgd2(payable - linesSum);
  if (Math.abs(residual) > PORTAL_INVOICE_LINE_MONEY_EPS) {
    if (residual > 0 && params.cardAdminFeeApplied) {
      lines.push({
        Description: "Credit card administrative fee",
        Quantity: 1,
        UnitAmount: residual,
      });
    } else if (lines.length > 0) {
      // Absorb sub-cent / rounding drift into the last line's unit amount.
      const last = lines[lines.length - 1]!;
      const lastLineTotal = roundSgd2(last.Quantity * last.UnitAmount);
      const adjustedTotal = roundSgd2(lastLineTotal + residual);
      last.UnitAmount = roundSgd2(adjustedTotal / last.Quantity);
    }
  }

  return lines;
}
